import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, CircleNotch, WarningCircle, MagnifyingGlass, MapPin, Package as PackageIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InventoryCard({ item, onUpdate }) {
  const isLow = item.quantity <= item.min_quantity;
  return (
    <div
      data-testid={`inventory-item-${item.id}`}
      className={`flex items-center justify-between p-3 border-b border-[#E5E5E5] last:border-0 ${isLow ? "bg-[#FF5A0008]" : ""}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          {isLow && <WarningCircle size={12} className="text-[#FF5A00] shrink-0" />}
          <p className="text-sm font-medium text-[#0A0A0A] truncate">{item.name}</p>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="font-mono-data text-[10px] text-[#404040] flex items-center gap-0.5">
            <MapPin size={8} /> {item.location.replace("_", " ").toUpperCase()}
          </span>
          <span className="font-mono-data text-[10px] text-[#404040]">
            {item.category.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="text-right shrink-0 ml-2">
        <div className={`font-mono font-bold text-lg ${isLow ? "text-[#FF5A00]" : "text-[#0A0A0A]"}`}>
          {item.quantity}
        </div>
        <div className="font-mono-data text-[10px] text-[#404040]">
          {item.unit} / min {item.min_quantity}
        </div>
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    name: "", category: "electrical", quantity: 0, min_quantity: 5,
    location: "main_shop", unit: "pcs", cost_per_unit: 0, supplier: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterLocation !== "all") params.location = filterLocation;
      const res = await axios.get(`${API}/inventory`, { params });
      setInventory(res.data);
    } catch {
      toast.error("Failed to load inventory");
    }
    setLoading(false);
  }, [filterCategory, filterLocation]);

  useEffect(() => { fetchInventory(); }, [fetchInventory]);

  const filtered = inventory.filter(i =>
    search === "" || i.name.toLowerCase().includes(search.toLowerCase())
  );
  const lowStock = inventory.filter(i => i.quantity <= i.min_quantity);
  const locations = [...new Set(inventory.map(i => i.location))];

  const createItem = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/inventory`, { ...form, quantity: parseInt(form.quantity), min_quantity: parseInt(form.min_quantity), cost_per_unit: parseFloat(form.cost_per_unit) });
      toast.success("Item added");
      setCreateOpen(false);
      setForm({ name: "", category: "electrical", quantity: 0, min_quantity: 5, location: "main_shop", unit: "pcs", cost_per_unit: 0, supplier: "" });
      fetchInventory();
    } catch {
      toast.error("Failed to add item");
    }
    setSaving(false);
  };

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Inventory
          </h1>
          {lowStock.length > 0 && (
            <p className="text-[10px] font-mono-data text-[#FF5A00]">
              {lowStock.length} ITEMS LOW STOCK
            </p>
          )}
        </div>
        <button
          data-testid="add-inventory-btn"
          onClick={() => setCreateOpen(true)}
          className="touch-target w-10 h-10 bg-[#002FA7] text-white rounded-sm flex items-center justify-center hover:bg-[#001D6C] transition-colors"
        >
          <Plus size={20} weight="bold" />
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#404040]" />
        <input
          data-testid="inventory-search"
          type="text" placeholder="Search inventory..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid="filter-inventory-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="electrical">Electrical</SelectItem>
            <SelectItem value="hvac">HVAC</SelectItem>
            <SelectItem value="tools">Tools</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid="filter-inventory-location">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            <SelectItem value="main_shop">Main Shop</SelectItem>
            <SelectItem value="vehicle_1">Vehicle 1</SelectItem>
            <SelectItem value="vehicle_2">Vehicle 2</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="all">
        <TabsList className="grid w-full grid-cols-2 h-9 mb-3">
          <TabsTrigger value="all" className="text-xs" data-testid="inventory-all-tab">All Items</TabsTrigger>
          <TabsTrigger value="low" className="text-xs" data-testid="inventory-low-tab">
            Low Stock {lowStock.length > 0 && `(${lowStock.length})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {loading ? (
            <div className="flex justify-center py-12">
              <CircleNotch size={28} className="animate-spin text-[#002FA7]" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#404040]">
              <PackageIcon size={32} className="mx-auto mb-2 text-[#E5E5E5]" />
              No items found.
            </div>
          ) : (
            <div className="wp-card p-0">
              {filtered.map(item => (
                <InventoryCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="low">
          {lowStock.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#34C759]">
              All items are above minimum stock levels.
            </div>
          ) : (
            <div className="wp-card p-0">
              {lowStock.map(item => (
                <InventoryCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Add Inventory Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={createItem} className="space-y-3">
            <input
              data-testid="inventory-name-input"
              type="text" placeholder="Item name"
              value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
            />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger data-testid="inventory-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="tools">Tools</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.location} onValueChange={v => setForm({...form, location: v})}>
                <SelectTrigger data-testid="inventory-location-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="main_shop">Main Shop</SelectItem>
                  <SelectItem value="vehicle_1">Vehicle 1</SelectItem>
                  <SelectItem value="vehicle_2">Vehicle 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-mono-data text-[#404040]">QTY</label>
                <input
                  data-testid="inventory-qty-input"
                  type="number" value={form.quantity}
                  onChange={e => setForm({...form, quantity: e.target.value})}
                  className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-data text-[#404040]">MIN</label>
                <input
                  data-testid="inventory-min-input"
                  type="number" value={form.min_quantity}
                  onChange={e => setForm({...form, min_quantity: e.target.value})}
                  className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-data text-[#404040]">UNIT</label>
                <input
                  data-testid="inventory-unit-input"
                  type="text" value={form.unit}
                  onChange={e => setForm({...form, unit: e.target.value})}
                  className="w-full px-2 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
            </div>
            <button
              data-testid="create-inventory-submit"
              type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add Item"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
